import { ApiError } from './apiClient.js';
import type { EntityConfig } from './masterDataConfig.js';

/**
 * Every master-data mutation failure the API can throw for a field problem
 * arrives as the *same* `code: 'VALIDATION_FAILED'` — a genuinely invalid
 * value, a duplicate code (409, still `VALIDATION_FAILED` —
 * `apps/api/src/master-data/{party,product,uom,tax,warehouse}.controller.ts`),
 * and a field the caller's role isn't allowed to write at all (422,
 * `assertMasterFieldsEditable` / `apps/api/src/master-data/guard.ts`,
 * `details: [{ path, message: 'Field is not editable.' }]`). The old client
 * copy collapsed all three into one "invalid or duplicate" guess, which is
 * flatly wrong for a permission denial — this reads the real signal
 * (HTTP status + the detail's own message, the only two things that
 * actually differ) instead of guessing.
 */
export function masterDataSubmitErrorMessage(err: unknown, config: EntityConfig): string {
  if (!(err instanceof ApiError) || err.code !== 'VALIDATION_FAILED') {
    return 'ذخیره ناموفق بود. دوباره تلاش کنید.';
  }

  const notEditableField = err.envelope.error.details?.find(
    (d) => d.message === 'Field is not editable.',
  );
  if (notEditableField) {
    const fieldLabel = config.formFields.find((f) => f.key === notEditableField.path)?.label;
    return fieldLabel
      ? `شما دسترسی ویرایش «${fieldLabel}» را ندارید.`
      : 'شما دسترسی ویرایش این فیلد را ندارید.';
  }

  // Duplicate-code conflicts are the one VALIDATION_FAILED case the API
  // marks with a distinct HTTP status (409, not 422) — real signal, not a
  // guess.
  if (err.status === 409) {
    return 'این کد قبلاً استفاده شده است.';
  }

  return 'اطلاعات وارد‌شده نامعتبر است.';
}
